import {useEffect} from 'react';
import type {IFormDataV2} from 'src/types/FormV2';
import type {
  ProactFormApi,
  UseProactFormOptions} from './useProactForm';
import {
  useProactForm,
} from './useProactForm';

export interface UseEntitySyncedFormOptions<TEntity>
  extends UseProactFormOptions {
  /** The loaded entity; undefined while its fetch is pending/skipped. */
  entity: TEntity | undefined;
  /** Builds the resetForm value payload once the entity has arrived. */
  toFormData: (entity: TEntity) => IFormDataV2;
  /** Builds the resetForm display-value payload, if the field set needs one. */
  toDisplayData?: (entity: TEntity) => IFormDataV2;
  /**
   * Extra side effects to run when the entity arrives, before resetForm
   * (e.g. mirroring the entity into local state used elsewhere on the page).
   */
  onEntityArrival?: (entity: TEntity) => void;
}

/**
 * Wraps useProactForm with the standard load-then-reset lifecycle: once
 * `entity` arrives, resets the form to `toFormData(entity)` (and runs
 * `onEntityArrival` first, for consumers that also mirror the entity into
 * local state). No-ops while `entity` is undefined.
 */
export function useEntitySyncedForm<TEntity>({
  entity,
  toFormData,
  toDisplayData,
  onEntityArrival,
  ...options
}: UseEntitySyncedFormOptions<TEntity>): ProactFormApi {
  const form = useProactForm(options);
  const {resetForm} = form;

  useEffect(() => {
    if (!entity) return;
    onEntityArrival?.(entity);
    resetForm(toFormData(entity), toDisplayData?.(entity));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, resetForm]);

  return form;
}
